import { format } from "date-fns";
import { Modal } from "../../ui/modal";
import Badge from "../../ui/badge/Badge";
import Button from "../../ui/button/Button";
import { Webhook } from "../../../stores/useWebhookStore";
import {
  getWebhookTransactionData,
  getUnifiedStatusBadgeColor,
  getWebhookStatusBadgeColor,
  toFixed,
} from "../../../utils/helpers";
import CopyButton from "../../ui/button/copyButton";
import { truncateAddress } from "../../../utils";
import { WALLET_STATUS } from "../../wallet/types";
import { PAYMENT_METHOD } from "../../../utils/constant";

interface WebhookDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  webhook: Webhook | null;
}

export default function WebhookDetailsModal({
  isOpen,
  onClose,
  webhook,
}: WebhookDetailsModalProps) {
  if (!webhook) return null;

  const transactionData = getWebhookTransactionData(webhook);

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "MMMM do yyyy, h:mm a");
  };

  const formatPaymentMethods = (paymentMethods: string) => {
    switch (paymentMethods) {
      case PAYMENT_METHOD.BANK_TRANSFER:
        return "BANK TRANSFER";
      case PAYMENT_METHOD.MOBILE_MONEY:
        return "MOBILE MONEY";
      case PAYMENT_METHOD.ZELLE:
        return "ZELLE";
      case PAYMENT_METHOD.INTERAC:
        return "INTERAC";
      case PAYMENT_METHOD.STABLE_CURRENCY:
        return "STABLE CURRENCY";
      case PAYMENT_METHOD.WALLET:
        return "WALLET";
      case PAYMENT_METHOD.WECHAT_PAY:
        return "WECHAT PAY";
      case PAYMENT_METHOD.ALIPAY:
        return "ALIPAY";
      default:
        return paymentMethods;
    }
  };

  const formatBanktransferProviders = (currency: string) => {
    switch (currency) {
      case "USD":
        return "Routing No";
      case "GBP":
        return "Sort Code";
      case "NGN":
        return "Bank Name";
      default:
        return "Provider/Bank Name";
    }
  };

  const renderPaymentDetails = (paymentDetails: any, title: string) => {
    if (!paymentDetails) return null;

    const paymentMethod = paymentDetails.paymentMethod;

    const renderStableCurrencyFields = () => (
      <>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">
            Wallet Address
          </span>
          <div className="flex items-center gap-2 max-w-[60%]">
            <p className="text-gray-800 dark:text-white/90 break-all text-right text-sm">
              {truncateAddress(paymentDetails.walletAddress)}
            </p>
            <CopyButton text={paymentDetails.walletAddress} />
          </div>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">Network</span>
          <span className="text-gray-800 dark:text-white/90">
            {paymentDetails.network}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">Currency</span>
          <span className="text-gray-800 dark:text-white/90">
            {paymentDetails.currency}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">
            Payment Method
          </span>
          <span className="text-gray-800 dark:text-white/90">
            {formatPaymentMethods(paymentMethod)}
          </span>
        </div>
      </>
    );

    const renderBankTransferFields = () => (
      <>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">Account Name</span>
          <span className="text-gray-800 dark:text-white/90">
            {paymentDetails.accountName}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">
            Account Number
          </span>
          <span className="text-gray-800 dark:text-white/90">
            {paymentDetails.accountNumber}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">
            {formatBanktransferProviders(paymentDetails.currency)}
          </span>
          <span className="text-gray-800 dark:text-white/90">
            {paymentDetails.providerName}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">Currency</span>
          <span className="text-gray-800 dark:text-white/90">
            {paymentDetails.currency}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">
            Payment Method
          </span>
          <span className="text-gray-800 dark:text-white/90">
            {formatPaymentMethods(paymentMethod)}
          </span>
        </div>
      </>
    );

    const renderMobileMoneyFields = () => (
      <>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">Account Name</span>
          <span className="text-gray-800 dark:text-white/90">
            {paymentDetails.accountName}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">
            Account Number
          </span>
          <span className="text-gray-800 dark:text-white/90">
            {paymentDetails.accountNumber}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">Network</span>
          <span className="text-gray-800 dark:text-white/90">
            {paymentDetails.providerName || paymentDetails.network}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">Currency</span>
          <span className="text-gray-800 dark:text-white/90">
            {paymentDetails.currency}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">
            Payment Method
          </span>
          <span className="text-gray-800 dark:text-white/90">
            {formatPaymentMethods(paymentMethod)}
          </span>
        </div>
      </>
    );

    const renderEmailPaymentFields = () => (
      <>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">Account Name</span>
          <span className="text-gray-800 dark:text-white/90">
            {paymentDetails.accountName}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">Email</span>
          <span className="text-gray-800 dark:text-white/90">
            {paymentDetails.email}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">Currency</span>
          <span className="text-gray-800 dark:text-white/90">
            {paymentDetails.currency}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">
            Payment Method
          </span>
          <span className="text-gray-800 dark:text-white/90">
            {formatPaymentMethods(paymentMethod)}
          </span>
        </div>
      </>
    );

    const renderFields = () => {
      switch (paymentMethod) {
        case "STABLE_CURRENCY":
          return renderStableCurrencyFields();
        case "BANK_TRANSFER":
          return renderBankTransferFields();
        case "MOBILE_MONEY":
          return renderMobileMoneyFields();
        case "ZELLE":
        case "INTERAC":
          return renderEmailPaymentFields();
        default:
          return (
            <>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">
                  Account Name
                </span>
                <span className="text-gray-800 dark:text-white/90">
                  {paymentDetails.accountName}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">
                  Account Number
                </span>
                <span className="text-gray-800 dark:text-white/90">
                  {paymentDetails.accountNumber}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">
                  Provider
                </span>
                <span className="text-gray-800 dark:text-white/90">
                  {paymentDetails.providerName}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">
                  Payment Method
                </span>
                <span className="text-gray-800 dark:text-white/90">
                  {formatPaymentMethods(paymentMethod)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">
                  Currency
                </span>
                <span className="text-gray-800 dark:text-white/90">
                  {paymentDetails.currency}
                </span>
              </div>
            </>
          );
      }
    };

    return (
      <div className="border-t pt-4 mt-4 space-y-3">
        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
          {title}
        </h4>
        {renderFields()}
      </div>
    );
  };

  const renderPaymentLinkDetails = () => (
    <>
      {/* Amount */}
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Amount</span>
        <span className="text-gray-800 dark:text-white/90">
          {(transactionData.amount || 0).toLocaleString()}{" "}
          {transactionData.paymentDetails?.currency}
        </span>
      </div>

      {/* Payment Link Status */}
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">
          Payment Link Status
        </span>
        <Badge
          size="sm"
          color={getUnifiedStatusBadgeColor(transactionData.status)}
        >
          {transactionData.status}
        </Badge>
      </div>

      {/* Payer Details */}
      {transactionData.payerDetails && (
        <div className="border-t pt-4 mt-4 space-y-3">
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
            Payer Details
          </h4>
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Name</span>
            <span className="text-gray-800 dark:text-white/90">
              {transactionData.payerDetails.name}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Email</span>
            <span className="text-gray-800 dark:text-white/90">
              {transactionData.payerDetails.email}
            </span>
          </div>
        </div>
      )}

      {/* Payment Details */}
      {transactionData.paymentDetails &&
        renderPaymentDetails(transactionData.paymentDetails, "Payment Details")}

      {/* Display Message */}
      {transactionData.displayMessage && (
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">Message</span>
          <span className="text-gray-800 dark:text-white/90 text-right">
            {transactionData.displayMessage}
          </span>
        </div>
      )}

      {/* Expiration */}
      {transactionData.expiresAt && (
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">Expires At</span>
          <span className="text-gray-800 dark:text-white/90">
            {formatDate(transactionData.expiresAt)}
          </span>
        </div>
      )}
    </>
  );

  const renderExchangeDetails = () => (
    <>
      {/* Amount */}
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Amount</span>
        <span className="text-gray-800 dark:text-white/90">
          {(transactionData.amount || 0).toLocaleString()}{" "}
          {transactionData.fromCurrency}
        </span>
      </div>

      {/* Value */}
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Value</span>
        <span className="text-gray-800 dark:text-white/90">
          {toFixed(
            (transactionData.amount || 0) * (transactionData.rate || 0),
            2,
          ).toLocaleString()}{" "}
          {transactionData.toCurrency}
        </span>
      </div>

      {/* Exchange Status */}
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">
          Exchange Status
        </span>
        <Badge
          size="sm"
          color={getUnifiedStatusBadgeColor(transactionData.status)}
        >
          {transactionData.status}
        </Badge>
      </div>

      {/* Currency Pair */}
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Currency Pair</span>
        <span className="text-gray-800 dark:text-white/90">
          {transactionData.fromCurrency}/{transactionData.toCurrency}
        </span>
      </div>

      {/* Rate */}
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Rate</span>
        <span className="text-gray-800 dark:text-white/90">
          {toFixed(transactionData.rate || 0, 2)}
        </span>
      </div>

      {/* Payment Details Sections */}
      {transactionData.buyerCreditPaymentDetails &&
        renderPaymentDetails(
          transactionData.buyerCreditPaymentDetails,
          "Buyer Credit Details",
        )}

      {transactionData.buyerDebitPaymentDetails &&
        renderPaymentDetails(
          transactionData.buyerDebitPaymentDetails,
          "Buyer Debit Details",
        )}

      {transactionData.sellerCreditPaymentDetails &&
        renderPaymentDetails(
          transactionData.sellerCreditPaymentDetails,
          "Seller Credit Details",
        )}
    </>
  );

  const renderWalletCreationDetails = () => (
    <>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Currency</span>
        <span className="text-gray-800 dark:text-white/90">
          {transactionData.currency}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Status</span>
        <Badge
          size="sm"
          color={
            transactionData.status === WALLET_STATUS.ACTIVE
              ? "success"
              : "light"
          }
        >
          {transactionData.status}
        </Badge>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Balance</span>
        <span className="text-gray-800 dark:text-white/90">
          {(transactionData.balance ?? 0).toLocaleString()}{" "}
          {transactionData.currency}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Held Balance</span>
        <span className="text-gray-800 dark:text-white/90">
          {(transactionData.heldBalance ?? 0).toLocaleString()}{" "}
          {transactionData.currency}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">
          Has Bank Account
        </span>
        <span className="text-gray-800 dark:text-white/90">
          {transactionData.hasBankAccount ? "Yes" : "No"}
        </span>
      </div>
    </>
  );

  const renderWalletTxnDetails = () => (
    <>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Amount</span>
        <span className="text-gray-800 dark:text-white/90">
          {(transactionData.amount ?? 0).toLocaleString()}{" "}
          {transactionData.currency}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Type</span>
        <Badge
          size="sm"
          color={transactionData.type === "CREDIT" ? "success" : "error"}
        >
          {transactionData.type}
        </Badge>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Status</span>
        <Badge
          size="sm"
          color={getUnifiedStatusBadgeColor(transactionData.status)}
        >
          {transactionData.status}
        </Badge>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Purpose</span>
        <span className="text-gray-800 dark:text-white/90">
          {transactionData.purpose}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Payment Method</span>
        <span className="text-gray-800 dark:text-white/90">
          {formatPaymentMethods(transactionData.paymentMethod ?? "")}
        </span>
      </div>
      {transactionData.narration && (
        <div className="flex justify-between gap-4">
          <span className="text-gray-500 dark:text-gray-400 shrink-0">
            Narration
          </span>
          <span className="text-gray-800 dark:text-white/90 text-right">
            {transactionData.narration}
          </span>
        </div>
      )}
      {transactionData.fee !== undefined && transactionData.fee > 0 && (
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">Fee</span>
          <span className="text-gray-800 dark:text-white/90">
            {transactionData.fee.toLocaleString()} {transactionData.currency}
          </span>
        </div>
      )}
      {transactionData.metadata?.receiver?.phoneNumber && (
        <div className="border-t pt-4 mt-4 space-y-3">
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
            Receiver
          </h4>
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">
              Phone Number
            </span>
            <span className="text-gray-800 dark:text-white/90">
              {transactionData.metadata.receiver.phoneNumber}
            </span>
          </div>
          {transactionData.metadata.receiver.network && (
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Network</span>
              <span className="text-gray-800 dark:text-white/90">
                {transactionData.metadata.receiver.network}
              </span>
            </div>
          )}
        </div>
      )}
      {transactionData.metadata?.senderName && (
        <div className="border-t pt-4 mt-4 space-y-3">
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
            Sender
          </h4>
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Name</span>
            <span className="text-gray-800 dark:text-white/90">
              {transactionData.metadata.senderName}
            </span>
          </div>
          {transactionData.metadata.senderAccountNumber && (
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">
                Account Number
              </span>
              <span className="text-gray-800 dark:text-white/90">
                {transactionData.metadata.senderAccountNumber}
              </span>
            </div>
          )}
        </div>
      )}
      {transactionData.paymentGatewayReference && (
        <div className="flex justify-between gap-4">
          <span className="text-gray-500 dark:text-gray-400 shrink-0">
            Gateway Ref
          </span>
          <span className="text-gray-800 dark:text-white/90 text-right text-sm break-all">
            {transactionData.paymentGatewayReference}
          </span>
        </div>
      )}
    </>
  );

  const renderVirtualAccountDetails = () => (
  <>
    <div className="flex justify-between">
      <span className="text-gray-500 dark:text-gray-400">Transaction ID</span>
      <span className="text-gray-800 dark:text-white/90 break-all">
        {transactionData.id || "N/A"}
      </span>
    </div>

    <div className="flex justify-between">
      <span className="text-gray-500 dark:text-gray-400">Wallet ID</span>
      <span className="text-gray-800 dark:text-white/90 break-all">
        {transactionData.walletId || "N/A"}
      </span>
    </div>

    <div className="flex justify-between">
      <span className="text-gray-500 dark:text-gray-400">Amount</span>
      <span className="text-gray-800 dark:text-white/90">
        {(transactionData.amount || 0).toLocaleString()}{" "}
        {transactionData.currency}
      </span>
    </div>

    <div className="flex justify-between">
      <span className="text-gray-500 dark:text-gray-400">Account Number</span>
      <span className="text-gray-800 dark:text-white/90">
        {transactionData.accountNumber || "N/A"}
      </span>
    </div>

    <div className="flex justify-between">
      <span className="text-gray-500 dark:text-gray-400">Account Reference</span>
      <span className="text-gray-800 dark:text-white/90 break-all">
        {transactionData.accountRef || "N/A"}
      </span>
    </div>

    <div className="flex justify-between">
      <span className="text-gray-500 dark:text-gray-400">Bank Code</span>
      <span className="text-gray-800 dark:text-white/90">
        {transactionData.bankCode || "N/A"}
      </span>
    </div>

    <div className="flex justify-between">
      <span className="text-gray-500 dark:text-gray-400">External Reference</span>
      <span className="text-gray-800 dark:text-white/90 break-all">
        {transactionData.externalReference || "N/A"}
      </span>
    </div>

    <div className="flex justify-between">
      <span className="text-gray-500 dark:text-gray-400">Valid For</span>
      <span className="text-gray-800 dark:text-white/90">
        {transactionData.validFor != null
          ? `${transactionData.validFor} seconds`
          : "N/A"}
      </span>
    </div>

    <div className="flex justify-between">
      <span className="text-gray-500 dark:text-gray-400">Expiry Date</span>
      <span className="text-gray-800 dark:text-white/90">
        {transactionData.expiryDate
          ? new Date(transactionData.expiryDate).toLocaleString()
          : "N/A"}
      </span>
    </div>

    <div className="flex justify-between">
      <span className="text-gray-500 dark:text-gray-400">Status</span>
      <Badge
        size="sm"
        color={getUnifiedStatusBadgeColor(transactionData.status)}
      >
        {transactionData.status || "N/A"}
      </Badge>
    </div>

    <div className="flex justify-between">
      <span className="text-gray-500 dark:text-gray-400">Created At</span>
      <span className="text-gray-800 dark:text-white/90">
        {transactionData.createdAt
          ? new Date(transactionData.createdAt).toLocaleString()
          : "N/A"}
      </span>
    </div>

    <div className="flex justify-between">
      <span className="text-gray-500 dark:text-gray-400">Updated At</span>
      <span className="text-gray-800 dark:text-white/90">
        {transactionData.updatedAt
          ? new Date(transactionData.updatedAt).toLocaleString()
          : "N/A"}
      </span>
    </div>
  </>
  );

  const renderStableWalletCreationDetails = () => (
    <>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Wallet ID</span>
        <span className="text-gray-800 dark:text-white/90">
          {transactionData.id}
        </span>
      </div>

      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">User ID</span>
        <span className="text-gray-800 dark:text-white/90">
          {transactionData.userId}
        </span>
      </div>

      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Wallet Type</span>
        <span className="text-gray-800 dark:text-white/90">
          {transactionData.type}
        </span>
      </div>

      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Parent ID</span>
        <span className="text-gray-800 dark:text-white/90">
          {transactionData.parentId || "N/A"}
        </span>
      </div>

      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Wallet Address</span>
        <span className="text-gray-800 dark:text-white/90 break-all">
          {transactionData.address || "N/A"}
        </span>
      </div>

      {transactionData.tronAddress && (
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">TRON Address</span>
          <span className="text-gray-800 dark:text-white/90 break-all">
            {transactionData.tronAddress}
          </span>
        </div>
      )}

      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Solana Address</span>
        <span className="text-gray-800 dark:text-white/90 break-all">
          {transactionData.solanaAddress || "N/A"}
        </span>
      </div>
    </>
  );

  const renderStableWalletTxnDetails = () => (
    <>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Transaction ID</span>
        <div className="flex items-center gap-2 max-w-[60%]">
          <span className="text-gray-800 dark:text-white/90 break-all text-right text-sm">
            {transactionData.id}
          </span>
        </div>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Wallet ID</span>
        <div className="flex items-center gap-2 max-w-[60%]">
          <span className="text-gray-800 dark:text-white/90 break-all text-right text-sm">
            {transactionData.walletId}
          </span>
        </div>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">User ID</span>
        <span className="text-gray-800 dark:text-white/90 break-all">
          {transactionData.userId}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">
          TxHash
        </span>
        <div className="flex items-center gap-2 max-w-[60%]">
          <span className="text-gray-800 dark:text-white/90 break-all text-right text-sm">
            {truncateAddress(transactionData.txHash, 10, 8)}
          </span>
          <CopyButton text={transactionData.txHash || ""} />
        </div>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Token</span>
        <span className="text-gray-800 dark:text-white/90">
          {transactionData.token}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Network</span>
        <span className="text-gray-800 dark:text-white/90">
          {transactionData.network}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Amount</span>
        <span className="text-gray-800 dark:text-white/90">
          {(transactionData.amount || 0).toLocaleString()}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Fee</span>
        <span className="text-gray-800 dark:text-white/90">
          {(transactionData.fee || 0).toLocaleString()}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Type</span>
        <span className="text-gray-800 dark:text-white/90">
          {transactionData.type}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">From</span>
        <div className="flex items-center gap-2 max-w-[60%]">
          <span className="text-gray-800 dark:text-white/90 break-all text-right text-sm">
            {truncateAddress(transactionData.from, 10, 8)}
          </span>
          <CopyButton text={transactionData.from || ""} />
        </div>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">To</span>
        <div className="flex items-center gap-2 max-w-[60%]">
          <span className="text-gray-800 dark:text-white/90 break-all text-right text-sm">
            {truncateAddress(transactionData.to, 10, 8)}
          </span>
          <CopyButton text={transactionData.to || ""} />
        </div>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">Status</span>
        <Badge
          size="sm"
          color={getUnifiedStatusBadgeColor(transactionData.status)}
        >
          {transactionData.status}
        </Badge>
      </div>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-lg mt-15">
      <div className="p-6 space-y-6">
        <h3 className="text-xl font-semibold text-gray-800 dark:text-white/90">
          Webhook Details
        </h3>

        <div className="max-h-[70vh] overflow-y-auto overflow-x-hidden pr-2 space-y-3">
          {/* Type Badge */}
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Type</span>
            <Badge
              size="sm"
              color={
                transactionData.category === "payment_link" ? "primary" : 
                transactionData.category === "exchange" ? "info" : 
                transactionData.category === "wallet_creation" ? "dark" : 
                transactionData.category === "wallet_txn" ? "light" : 
                transactionData.category === "stable_wallet_creation" ? "dark" :
                transactionData.category === "stable_wallet_txn" ? "light" : 
               transactionData.category === "wallet_virtual_account" ? "info" :
                "light"
              }
            >
              { transactionData.category === "payment_link" ? "Payment Link" : 
                transactionData.category === "exchange" ? "Exchange" : 
                transactionData.category === "wallet_creation" ? "Wallet Creation" : 
                transactionData.category === "wallet_txn" ? "Wallet Transaction" :
                transactionData.category === "stable_wallet_creation" ? "Stable Wallet Creation" :
                transactionData.category === "stable_wallet_txn" ? "Stable Wallet Transaction" : 
                transactionData.category === "wallet_virtual_account" ? "Wallet Virtual Account" :
                "Unknown"
              }
            </Badge>
          </div>

          {/* Webhook Status */}
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">
              Webhook Status
            </span>
            <Badge size="sm" color={getWebhookStatusBadgeColor(webhook.status)}>
              {webhook.status}
            </Badge>
          </div>

          {/* Retry Count */}
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">
              Retry Count
            </span>
            <span className="text-gray-800 dark:text-white/90">
              {webhook.trialCount}
            </span>
          </div>

          {/* Created At */}
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Created</span>
            <span className="text-gray-800 dark:text-white/90">
              {formatDate(webhook.createdAt)}
            </span>
          </div>

          {/* Last Attempt */}
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">
              Last Retry Attempt
            </span>
            <span className="text-gray-800 dark:text-white/90">
              {formatDate(webhook.lastTriedAt)}
            </span>
          </div>

          {/* Dynamic Content Based on Category */}
          {transactionData.category === "payment_link" && renderPaymentLinkDetails()}
          {transactionData.category === "exchange" && renderExchangeDetails()}
          {transactionData.category === "wallet_creation" && renderWalletCreationDetails()}
          {transactionData.category === "wallet_txn" && renderWalletTxnDetails()}
          {transactionData.category === "stable_wallet_creation" && renderStableWalletCreationDetails()}
          {transactionData.category === "stable_wallet_txn" && renderStableWalletTxnDetails()}
          {transactionData.category === "wallet_virtual_account" && renderVirtualAccountDetails()}
          {transactionData.category === "unknown" && (
            <div className="text-center py-4 text-gray-500">
              Unknown transaction type
            </div>
          )}
        </div>

        {/* Close Button */}
        <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
